import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import LanguageDropdown from '../components/LanguageDropdown';

const Login = () => {
  const { login, API } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      login(res.data.access_token, res.data.user);
      toast.success(t('messages.success'));
    } catch (error) {
      const backendMessage = error.response?.data?.detail;
      // Map known backend messages to translation keys
      let errorMessage = t('messages.error');
      if (backendMessage) {
        if (backendMessage.includes('verify your email')) {
          errorMessage = t('messages.notVerified');
        } else if (backendMessage.includes('Invalid credentials') || backendMessage.includes('Invalid email or password')) {
          errorMessage = t('messages.invalidCredentials');
        } else {
          errorMessage = backendMessage;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700 text-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-16 -left-16 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl" />

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <a href="/">
            <img src="/idemenu-logo.svg" alt="idemenu" className="h-10 w-auto cursor-pointer" />
          </a>
          <LanguageDropdown variant="light" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight">
              {t('auth.loginTitle')}
            </h1>
            <p className="text-lg text-purple-100 leading-relaxed">
              {t('auth.loginSubtitle')}
            </p>
            <div className="space-y-3 text-purple-100">
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.loginFeature1')}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.loginFeature2')}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1">✓</span>
                <span>{t('auth.loginFeature3')}</span>
              </div>
            </div>
            <div className="text-sm text-purple-100">
              {t('auth.noAccount')} <Link to="/signup" className="font-semibold text-white underline">{t('auth.createFreeAccount')}</Link>
            </div>
          </div>

          {/* Form card */}
          <div className="bg-white text-gray-900 rounded-2xl shadow-2xl p-8 sm:p-10 relative">
            <div className="absolute inset-0 rounded-2xl border border-white/60 pointer-events-none" />
            <div className="flex flex-col gap-2 mb-8">
              <div className="text-sm font-semibold text-purple-700">{t('auth.welcome')}</div>
              <h2 className="text-2xl font-bold">{t('auth.login')}</h2>
              <p className="text-gray-600">{t('auth.loginDescription')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-semibold text-gray-700">{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-semibold text-gray-700">{t('auth.password')}</label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-60"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? t('messages.loading') : t('auth.login')}
              </button>
            </form>

            <div className="mt-6 text-sm text-gray-600">
              {t('auth.noAccount')} <Link to="/signup" className="font-semibold text-purple-700 hover:underline">{t('auth.createFreeAccount')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
